import type { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useExitWebView } from '@onl-web/shared/hooks'

import { APP_REDIRECT_URL } from '@/constants'

import { reportError } from './reportError'

interface GlobalErrorBoundaryProps {
  children: ReactNode
}

const FALLBACK_TITLE = '일시적인 오류가 발생했어요'
const FALLBACK_DESCRIPTION = '앱을 다시 실행해주세요'

function GlobalErrorFallback() {
  const exitWebView = useExitWebView(APP_REDIRECT_URL)

  const handleConfirm = () => {
    try {
      exitWebView()
    } catch {
      window.location.href = APP_REDIRECT_URL
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100dvh',
        gap: '16px',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <p style={{ fontSize: '18px', fontWeight: 600 }}>{FALLBACK_TITLE}</p>
      <p style={{ fontSize: '14px', color: '#666' }}>{FALLBACK_DESCRIPTION}</p>
      <button
        onClick={handleConfirm}
        style={{
          marginTop: '8px',
          padding: '12px 24px',
          borderRadius: '8px',
          background: '#222',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        확인
      </button>
    </div>
  )
}

export default function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  const handleError = (error: unknown, info: { componentStack?: string | null }) => {
    reportError(error, {
      source: 'render',
      level: 'fatal',
      componentStack: info.componentStack,
    })
  }

  return (
    <ErrorBoundary
      FallbackComponent={GlobalErrorFallback}
      onError={handleError}>
      {children}
    </ErrorBoundary>
  )
}
